/* Stories — bubble rail + full-screen reel. Paired with sections/maahi-stories.liquid.
 *
 * Kept out of the section's own markup so the theme editor can re-run it:
 * script tags inside section HTML that Shopify swaps in on
 * shopify:section:load never execute, which would leave the editor preview
 * dead after every setting change.
 *
 * Progress is driven by requestAnimationFrame rather than a CSS transition, so
 * pausing is exact and a video's real duration can drive its own bar — an
 * eight-second clip gets an eight-second bar instead of a fixed guess.
 */
(function () {
  var SEEN_KEY = 'maahi:stories:seen';
  var HOLD_MS = 220;

  function readSeen() {
    try {
      return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function writeSeen(seen) {
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    } catch (e) {
      /* Private mode, quota, or storage disabled — the ring just stays lit. */
    }
  }

  function Stories(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    /* Look inside this section first. A previously portaled viewer is still in
       the document, and plain document order would hand us that stale node. */
    var section = root.closest('.shopify-section') || document;
    this.viewer = section.querySelector('[data-story-viewer="' + this.sectionId + '"]');
    if (!this.viewer) {
      this.viewer = document.querySelector('body > [data-story-viewer="' + this.sectionId + '"]');
    }
    if (!this.viewer) return;

    /* Portal to <body>.
       The section wrapper Shopify emits for the header group carries
       `z-index: 7`, which makes it a stacking context — so the viewer's own
       z-index counted only *within* that box and lost to the header section at
       `z-index: 8`. Taps meant for close, pause and the progress bar were
       landing on the header's cart icon, menu button and logo instead. No
       z-index on the viewer can win that from the inside; it has to leave the
       box. */
    if (this.viewer.parentElement !== document.body) {
      var stale = document.querySelector('body > [data-story-viewer="' + this.sectionId + '"]');
      if (stale && stale !== this.viewer) stale.remove();
      document.body.appendChild(this.viewer);
    }

    /* Not root.querySelectorAll: in header-trigger mode the only opener is the
       mark in the header, which is nowhere near this section's markup. */
    this.bubbles = Array.prototype.slice.call(document.querySelectorAll('[data-story-open]'));
    this.slides = Array.prototype.slice.call(this.viewer.querySelectorAll('[data-story-index]'));
    this.bars = Array.prototype.slice.call(this.viewer.querySelectorAll('.maahi-story-viewer__bar i'));
    this.arrows = {
      prev: this.viewer.querySelector('.maahi-story-viewer__arrow--prev'),
      next: this.viewer.querySelector('.maahi-story-viewer__arrow--next')
    };
    this.imageDuration = (parseFloat(root.dataset.imageDuration) || 5) * 1000;

    this.index = -1;
    this.paused = false;
    this.raf = null;
    this.elapsed = 0;
    this.lastTick = 0;
    this.lastFocus = null;

    this.paintSeen();
    this.bind();
  }

  Stories.prototype.paintSeen = function () {
    var seen = readSeen();
    this.bubbles.forEach(function (b) {
      /* The header mark is a permanent call to action — greying it out once
         watched would defeat the point of the pulse. */
      if (b.hasAttribute('data-story-trigger')) return;
      b.dataset.seen = seen[b.dataset.storyKey] ? 'true' : 'false';
    });
  };

  Stories.prototype.markSeen = function (i) {
    var bubble = this.bubbles[i];
    if (!bubble || bubble.hasAttribute('data-story-trigger')) return;
    var seen = readSeen();
    seen[bubble.dataset.storyKey] = 1;
    writeSeen(seen);
    bubble.dataset.seen = 'true';
  };

  Stories.prototype.bind = function () {
    var self = this;


    this.viewer.addEventListener('click', function (event) {
      if (event.target.closest('[data-story-close]')) { self.close(); return; }
      if (event.target.closest('[data-story-pause]')) { self.togglePause(); return; }
      /* A hold is a pause, not a tap — don't also advance when it ends. */
      if (self.held) { self.held = false; return; }
      if (event.target.closest('[data-story-next]')) { self.go(1); return; }
      if (event.target.closest('[data-story-prev]')) { self.go(-1); }
    });

    ['[data-story-next]', '[data-story-prev]'].forEach(function (sel) {
      Array.prototype.forEach.call(self.viewer.querySelectorAll(sel), function (zone) {
        var timer = null;
        var start = function () {
          timer = setTimeout(function () { self.held = true; self.setPaused(true); }, HOLD_MS);
        };
        var end = function () {
          clearTimeout(timer);
          if (self.held) self.setPaused(false);
        };
        zone.addEventListener('pointerdown', start);
        zone.addEventListener('pointerup', end);
        zone.addEventListener('pointercancel', end);
        zone.addEventListener('pointerleave', end);
      });
    });

    /* Swipe: sideways moves between stories, a downward flick closes. */
    var x0 = 0, y0 = 0, tracking = false;
    this.viewer.addEventListener('touchstart', function (e) {
      if (!e.touches.length) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; tracking = true;
    }, { passive: true });

    this.viewer.addEventListener('touchend', function (e) {
      if (!tracking || !e.changedTouches.length) return;
      tracking = false;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) { self.go(dx < 0 ? 1 : -1); }
      else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { self.close(); }
    }, { passive: true });

  };

  /* Bound once for the page, dispatched to whichever instance is live. The
     theme editor re-renders sections in place, and per-instance document
     listeners would pile up one set per edit. */
  var current = null;
  var documentBound = false;

  function bindDocument() {
    if (documentBound) return;
    documentBound = true;

    /* Delegated, so a trigger rendered by the header section — which loads
       independently of the stories section — works whatever the order. */
    document.addEventListener('click', function (event) {
      if (!current || !event.target.closest) return;
      var opener = event.target.closest('[data-story-open]');
      if (!opener || current.viewer.dataset.open === 'true') return;
      event.preventDefault();
      current.open(parseInt(opener.dataset.storyOpen, 10) || 0);
    });

    document.addEventListener('keydown', function (event) {
      if (!current || current.viewer.dataset.open !== 'true') return;
      if (event.key === 'Escape') { current.close(); }
      else if (event.key === 'ArrowRight') { current.go(1); }
      else if (event.key === 'ArrowLeft') { current.go(-1); }
      else if (event.key === ' ' || event.key === 'Spacebar') { event.preventDefault(); current.togglePause(); }
      else if (event.key === 'Tab') { current.trapFocus(event); }
    });

    /* A backgrounded tab should not burn through the reel. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && current && current.viewer.dataset.open === 'true') current.setPaused(true);
    });
  }

  Stories.prototype.trapFocus = function (event) {
    var focusable = this.viewer.querySelectorAll('button, a[href]');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  Stories.prototype.open = function (index) {
    this.lastFocus = document.activeElement;
    this.viewer.hidden = false;
    /* Next frame, so the opacity/scale transition has a starting point. */
    var self = this;
    requestAnimationFrame(function () { self.viewer.dataset.open = 'true'; });
    document.documentElement.style.overflow = 'hidden';
    /* The chat launcher is fixed at z-index 2147483600, above this viewer, and
       lands right on the CTA. Rather than escalate z-index against it, flag the
       open state on <html> and let CSS take it out of the way. */
    document.documentElement.classList.add('maahi-story-open');
    this.show(index);
    var close = this.viewer.querySelector('[data-story-close]');
    if (close && close.focus) close.focus();
  };

  Stories.prototype.close = function () {
    this.stop();
    this.viewer.dataset.open = 'false';
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('maahi-story-open');
    var slide = this.slides[this.index];
    if (slide) {
      var media = slide.querySelector('video');
      if (media) media.pause();
      /* Clear it as well as hiding the viewer: an active slide has
         pointer-events: auto, so leaving one set turns the closed overlay into
         an invisible click-catcher over the whole page. */
      slide.dataset.active = 'false';
    }
    var self = this;
    setTimeout(function () { self.viewer.hidden = true; }, 280);
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    this.index = -1;
  };

  /* Only the current slide and its neighbour get real sources, so opening the
     rail does not pull every video at once. */
  Stories.prototype.hydrate = function (i) {
    [i, i + 1].forEach(function (n) {
      var slide = this.slides[n];
      if (!slide) return;
      var media = slide.querySelector('[data-story-media]');
      if (!media || media.dataset.hydrated) return;
      if (media.tagName === 'VIDEO') {
        Array.prototype.forEach.call(media.querySelectorAll('source'), function (s) {
          if (s.dataset.src) s.src = s.dataset.src;
        });
        media.load();
      } else if (media.dataset.src) {
        media.src = media.dataset.src;
      }
      media.dataset.hydrated = '1';
    }, this);
  };

  Stories.prototype.show = function (i) {
    if (i < 0 || i >= this.slides.length) return;
    this.stop();

    var previous = this.slides[this.index];
    if (previous) {
      previous.dataset.active = 'false';
      var pv = previous.querySelector('video');
      if (pv) { pv.pause(); pv.currentTime = 0; }
    }

    this.index = i;
    this.hydrate(i);
    this.markSeen(i);

    this.bars.forEach(function (bar, n) {
      bar.style.transform = 'scaleX(' + (n < i ? 1 : 0) + ')';
    });

    var slide = this.slides[i];
    slide.dataset.active = 'true';

    if (this.arrows.prev) this.arrows.prev.disabled = i === 0;
    if (this.arrows.next) this.arrows.next.disabled = i === this.slides.length - 1;

    var video = slide.querySelector('video');
    this.elapsed = 0;
    this.setPaused(false);

    if (video) {
      var self = this;
      var play = function () {
        self.duration = (video.duration && isFinite(video.duration) ? video.duration * 1000 : self.imageDuration);
        var p = video.play();
        if (p && p.catch) p.catch(function () { /* autoplay blocked; the bar still runs */ });
        self.start();
      };
      if (video.readyState >= 1) play();
      else video.addEventListener('loadedmetadata', play, { once: true });
    } else {
      this.duration = this.imageDuration;
      this.start();
    }
  };

  Stories.prototype.go = function (step) {
    var next = this.index + step;
    if (next < 0) return;
    if (next >= this.slides.length) { this.close(); return; }
    this.show(next);
  };

  Stories.prototype.start = function () {
    var self = this;
    this.lastTick = performance.now();
    var tick = function (now) {
      if (self.paused) { self.lastTick = now; self.raf = requestAnimationFrame(tick); return; }
      /* Clamp the frame delta. rAF stops firing when the tab is backgrounded
         or the app is switched away, and it does not always fire
         visibilitychange on the way out — so the first frame back can carry a
         delta of many seconds and fast-forward the reel to the end before the
         customer has seen anything. 250ms is ~15 dropped frames, far more than
         any real hitch, and far less than a story. */
      self.elapsed += Math.min(now - self.lastTick, 250);
      self.lastTick = now;
      var ratio = Math.min(1, self.elapsed / self.duration);
      if (self.bars[self.index]) self.bars[self.index].style.transform = 'scaleX(' + ratio + ')';
      if (ratio >= 1) { self.go(1); return; }
      self.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  };

  Stories.prototype.stop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  Stories.prototype.setPaused = function (state) {
    this.paused = state;
    this.viewer.dataset.paused = state ? 'true' : 'false';
    var slide = this.slides[this.index];
    var video = slide && slide.querySelector('video');
    if (video) {
      if (state) video.pause();
      else { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
    }
    var btn = this.viewer.querySelector('[data-story-pause]');
    if (btn) btn.setAttribute('aria-label', state ? 'Play story' : 'Pause story');
  };

  Stories.prototype.togglePause = function () {
    this.setPaused(!this.paused);
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-maahi-stories]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.storiesReady) return;
      root.dataset.storiesReady = '1';
      var made = new Stories(root);
      if (made.viewer) {
        current = made;
        bindDocument();
        /* Reveals the header trigger. It ships hidden so that a stories section
           with no blocks — which never loads this script — cannot leave a button
           in the header that opens nothing. */
        document.documentElement.classList.add('maahi-stories-ready');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: re-init when a section is added or its settings change. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
